// Copyright ©️ 2026 eVolve MEP, LLC

import type { TaskType } from '@evolvemep/api-ts';
import { Badge, Checkbox, Flex, Stack, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { AttachmentsPopover } from 'components/Mantine/Attachments/AttachmentsPopover';
import { convertSecondsToDuration } from 'components/Mantine/durationHelpers';
import { ImageFromFile } from 'components/Mantine/FileUploads/ImageFromFile';
import { GridMenu } from 'components/Mantine/GridMenu';
import { isNotNil } from 'helpers/isNotNil';

type TaskTypeListItemProps = {
  taskType: TaskType;
  onTaskSelectToggle: (toggleTask: TaskType) => void;
  isSelected: boolean;
  onEditTaskType: (taskType: TaskType) => void;
  onDeleteTaskType: (taskType: TaskType) => void;
};

export const TaskTypeListItem = ({
  onTaskSelectToggle,
  isSelected,
  taskType,
  onEditTaskType,
  onDeleteTaskType,
}: TaskTypeListItemProps) => {
  const disabled = taskType.isAssignedToWorkCell;

  return (
    <Flex
      align="center"
      h={70}
      justify="space-between"
      mx="md"
      my="sm"
      onClick={!disabled ? () => onTaskSelectToggle(taskType) : undefined}
    >
      <Flex gap="sm" align="center" justify="left" flex={1}>
        <Checkbox disabled={disabled} checked={isSelected} />
        <Flex gap="sm" flex={1}>
          {isNotNil(taskType.image) && (
            <ImageFromFile
              fileId={taskType.image.fileId}
              alt={taskType.name}
              size={80}
              withContainer
              fallback={<EvolveIcon icon="WorkCellTask" color="gray" />}
            />
          )}
          <Stack
            fz="sm"
            gap="xs"
            w="100%"
            justify="center"
            pr="lg"
            style={{
              userSelect: 'none',
            }}
          >
            <Flex gap="sm" align="center">
              <Text lineClamp={1}>{taskType.name}</Text>
              {taskType.isAssignedToWorkCell && (
                <AgGridStyleTooltip label="Assigned to a work cell">
                  <EvolveIcon icon="Pin" color="primary" size="sm" />
                </AgGridStyleTooltip>
              )}
              {isNotNil(taskType.estimatedUnitTime) && (
                <AgGridStyleTooltip label="Estimated Unit Time">
                  <Badge variant="default">
                    <Flex>
                      <Text span inherit c="dimmed">
                        {convertSecondsToDuration(taskType.estimatedUnitTime)}
                      </Text>
                    </Flex>
                  </Badge>
                </AgGridStyleTooltip>
              )}
            </Flex>
            {taskType.description && (
              <Text size="sm" c="dimmed" lineClamp={2}>
                {taskType.description}
              </Text>
            )}
          </Stack>
        </Flex>
      </Flex>
      <Flex align="center">
        <AttachmentsPopover taskTypeId={taskType.taskTypeId} numberOfAttachments={taskType.attachmentCount} />
        <GridMenu
          options={['Edit', 'Delete']}
          onMenuItemSelected={(option) => {
            if (option === 'Edit') onEditTaskType(taskType);
            else onDeleteTaskType(taskType);
          }}
        />
      </Flex>
    </Flex>
  );
};
